import { createFrameCaptureVideo, releaseMediaElement } from '@/lib/preview/frameCaptureVideo';

type VideoAspect = 'horizontal' | 'vertical';
const aspectRequests = new Map<string, Promise<VideoAspect>>();

/** Share metadata probes and release their network/decoder resources immediately.
 * Successful results are cached; failed/timeout probes can be retried later. */
export const getVideoAspectRatio = (videoUrl: string): Promise<VideoAspect> => {
  if (!videoUrl) return Promise.resolve('horizontal');
  const existing = aspectRequests.get(videoUrl);
  if (existing) return existing;
  const request = new Promise<VideoAspect>((resolve) => {
    const video = createFrameCaptureVideo();
    let settled = false;
    const finish = (format: VideoAspect, cache: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      video.removeEventListener('loadedmetadata', onMetadata);
      video.removeEventListener('error', onError);
      releaseMediaElement(video);
      if (!cache) aspectRequests.delete(videoUrl);
      resolve(format);
    };
    const onMetadata = () => {
      const valid = video.videoWidth > 0 && video.videoHeight > 0;
      finish(valid && video.videoWidth < video.videoHeight ? 'vertical' : 'horizontal', valid);
    };
    const onError = () => finish('horizontal', false);
    const timer = setTimeout(onError, 5000);
    video.addEventListener('loadedmetadata', onMetadata);
    video.addEventListener('error', onError);
    video.src = videoUrl;
    video.load();
  });
  aspectRequests.set(videoUrl, request);
  while (aspectRequests.size > 32) aspectRequests.delete(aspectRequests.keys().next().value!);
  return request;
};

export const getAvailableExportFormats = async (videoUrl: string): Promise<VideoAspect[]> => {
  const aspect = await getVideoAspectRatio(videoUrl);
  return aspect === 'vertical' ? ['vertical'] : ['horizontal', 'vertical'];
};
